import { useMemo, useState } from "react";
import type {
  LandRegionData,
  LandRegionDataDistrict,
  LandRegionLandYear,
  LandRegionSectorFull,
  LandRegionSrc,
} from "@/lib/api";
import { cn } from "@/lib/cn";
import { useEvidence } from "./evidence";
import { signedPct, srcEvidence, ValueButton } from "./regionshared";
import { fmtNum } from "./shared";

/* -------------------------------------------------------------------------- */
/* 逐年数据: 城市逐年总表 / 区县逐年趋势 / 分行业逐年                               */
/* -------------------------------------------------------------------------- */

const MAX_YEARS = 7;
/** 近三年均值比此前三年变动超过这么多个百分点, 记为加快 / 放缓(与研判规则一致) */
const TREND_PP = 1.5;
const LAND_FORMULA = "国信网招拍挂成交逐宗明细（工业、仓储用地）按出让年份汇总；地价 = 成交总价 ÷ 成交面积；不含协议出让和划拨";

type Series = Record<string, number | null | undefined>;

function windowYears(list: Series[], max = MAX_YEARS): string[] {
  const set = new Set<string>();
  for (const s of list) {
    for (const [y, v] of Object.entries(s ?? {})) if (v != null && /^\d{4}$/.test(y)) set.add(y);
  }
  return Array.from(set).sort().slice(-max);
}

interface RateTrend {
  recent: number;
  recentSpan: string;
  early: number | null;
  earlySpan: string;
  change: number | null;
}

/** 近三年(须连续齐全)均值 vs 此前三年(至少两年)均值, 与后端 _growth_trend 同一算法 */
function rateTrend(series: Series | undefined): RateTrend | null {
  const val = (y: number): number | null | undefined => series?.[String(y)];
  const ys = Object.keys(series ?? {})
    .filter((y) => series?.[y] != null)
    .map(Number)
    .sort((a, b) => a - b);
  const last = ys[ys.length - 1];
  if (last == null) return null;
  const recent = [last - 2, last - 1, last];
  if (recent.some((y) => val(y) == null)) return null;
  const avg = (list: number[]): number => list.reduce((a, y) => a + (val(y) as number), 0) / list.length;
  const early = [last - 5, last - 4, last - 3].filter((y) => val(y) != null);
  const r = avg(recent);
  const e = early.length >= 2 ? avg(early) : null;
  return {
    recent: r,
    recentSpan: `${recent[0]}–${last}`,
    early: e,
    earlySpan: early.length >= 2 ? `${early[0]}–${early[early.length - 1]}` : "",
    change: e == null ? null : r - e,
  };
}

function trendWord(change: number | null | undefined): string {
  if (change == null) return "";
  return change >= TREND_PP ? "加快" : change <= -TREND_PP ? "放缓" : "基本平稳";
}

function pp(n: number | null | undefined): string {
  if (n == null || Number.isNaN(n)) return "—";
  return `${n > 0 ? "+" : ""}${n.toFixed(1)} 个百分点`;
}

/** 走势说明文字, 如「近三年（2023–2025）平均 2.4%，此前三年（2020–2022）平均 4.4%，放缓 2.0 个百分点」 */
function trendSentence(t: RateTrend | null): string {
  if (!t) return "";
  let text = `近三年（${t.recentSpan}）平均 ${t.recent.toFixed(1)}%`;
  if (t.early != null && t.change != null) {
    const word = trendWord(t.change);
    text += `，此前三年（${t.earlySpan}）平均 ${t.early.toFixed(1)}%，${word}${word === "基本平稳" ? "" : ` ${Math.abs(t.change).toFixed(1)} 个百分点`}`;
  }
  return text;
}

function yoyOf(series: Series, year: string): number | null {
  const cur = series[year];
  const prev = series[String(Number(year) - 1)];
  if (cur == null || prev == null || prev <= 0) return null;
  return (cur / prev - 1) * 100;
}

function cagrOf(series: Series, years: string[]): { pct: number; span: string } | null {
  const ys = years.filter((y) => series[y] != null);
  const y0 = ys[0];
  const y1 = ys[ys.length - 1];
  if (y0 == null || y1 == null || y0 === y1) return null;
  const a = series[y0] as number;
  const b = series[y1] as number;
  const n = Number(y1) - Number(y0);
  if (a <= 0 || b <= 0 || n <= 0) return null;
  return { pct: ((b / a) ** (1 / n) - 1) * 100, span: `${y0}→${y1}` };
}

function landSeries(byYear: Record<string, LandRegionLandYear> | undefined, pick: (x: LandRegionLandYear) => number | null): Series {
  return Object.fromEntries(Object.entries(byYear ?? {}).map(([y, v]) => [y, pick(v)]));
}

export function Sparkline({
  values,
  highlight = false,
  zero = false,
}: {
  values: Array<number | null | undefined>;
  highlight?: boolean;
  zero?: boolean;
}): JSX.Element | null {
  const pts = values.flatMap((v, i) => (v == null ? [] : [[i, v] as [number, number]]));
  const lastPt = pts[pts.length - 1];
  if (pts.length < 2 || !lastPt) return null;
  const W = 76;
  const H = 22;
  const pad = 2.5;
  let lo = Math.min(...pts.map(([, v]) => v));
  let hi = Math.max(...pts.map(([, v]) => v));
  if (zero) {
    lo = Math.min(lo, 0);
    hi = Math.max(hi, 0);
  }
  const span = hi - lo || 1;
  const x = (i: number): number => pad + (i / Math.max(1, values.length - 1)) * (W - pad * 2);
  const y = (v: number): number => H - pad - ((v - lo) / span) * (H - pad * 2);
  const d = pts.map(([i, v], k) => `${k ? "L" : "M"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const [li, lv] = lastPt;
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="block" aria-hidden="true">
      {zero && lo < 0 && hi > 0 ? (
        <line x1={pad} x2={W - pad} y1={y(0)} y2={y(0)} className="stroke-zinc-300" strokeWidth={1} strokeDasharray="2 2" />
      ) : null}
      <path d={d} fill="none" strokeWidth={1.5} strokeLinejoin="round" className={highlight ? "stroke-brand-600" : "stroke-zinc-400"} />
      <circle cx={x(li)} cy={y(lv)} r={2} className={highlight ? "fill-brand-600" : "fill-zinc-500"} />
    </svg>
  );
}

function rateClass(v: number | null | undefined): string {
  return v != null && v < 0 ? "text-rose-600" : "";
}

/* -------------------------------------------------------------------------- */
/* 城市逐年总表                                                                 */
/* -------------------------------------------------------------------------- */

interface CityLine {
  key: string;
  label: string;
  unit: string;
  digits: number;
  kind: "rate" | "level" | "count";
  series: Series;
  rows?: LandRegionSrc[];
  formula?: string;
}

export function CityYearlyTable({ rd, city }: { rd: LandRegionData; city: string }): JSX.Element | null {
  const evidence = useEvidence();
  const cp = rd.city_profile;
  const src = cp.sources ?? {};
  const land = cp.land_by_year;
  const incomplete = new Set(rd.land_meta?.incomplete_years ?? []);
  const lines: CityLine[] = (
    [
      { key: "gdp", label: "GDP", unit: "亿元", digits: 0, kind: "level", series: cp.gdp?.series ?? {}, rows: src.gdp },
      { key: "gdp_real", label: "GDP 实际增速", unit: "%", digits: 1, kind: "rate", series: cp.gdp?.real_growth ?? {}, rows: src.gdp_growth },
      { key: "ind", label: "工业增加值增速", unit: "%", digits: 1, kind: "rate", series: cp.industry_growth?.series ?? {}, rows: src.industry_growth },
      { key: "pop", label: "常住人口", unit: "万人", digits: 1, kind: "level", series: cp.population?.series ?? {}, rows: src.population },
      { key: "output", label: "规上工业总产值", unit: "亿元", digits: 0, kind: "level", series: cp.industrial_output?.series ?? {}, rows: src.industrial_output },
      { key: "firms", label: "规上工业企业", unit: "家", digits: 0, kind: "level", series: cp.above_scale_firms?.series ?? {}, rows: src.above_scale_firms },
      { key: "nr", label: "新登记企业", unit: cp.new_registrations?.unit || "户", digits: 0, kind: "level", series: cp.new_registrations?.series ?? {}, rows: src.new_registrations },
      { key: "lp", label: "工业用地成交", unit: "宗", digits: 0, kind: "count", series: landSeries(land, (x) => x.parcels), formula: LAND_FORMULA },
      { key: "la", label: "工业用地面积", unit: "亩", digits: 0, kind: "count", series: landSeries(land, (x) => x.area_mu), formula: LAND_FORMULA },
      { key: "lpr", label: "工业地价", unit: "万元/亩", digits: 1, kind: "level", series: landSeries(land, (x) => x.avg_price_wan_per_mu), formula: LAND_FORMULA },
    ] as CityLine[]
  ).filter((l) => Object.values(l.series).some((v) => v != null));
  const years = windowYears(lines.map((l) => l.series));
  if (!lines.length || years.length < 2) return null;
  const trend = rateTrend(cp.gdp?.real_growth);

  return (
    <div className="mt-5">
      <div className="mb-2 text-[13px] font-medium text-zinc-700">逐年数据</div>
      <div className="overflow-x-auto rounded-lg border border-cardline">
        <table className="w-full min-w-[720px] text-[13px]">
          <thead>
            <tr className="bg-cardhead text-[12px] text-zinc-500">
              <th className="sticky left-0 z-10 bg-cardhead px-3 py-2 text-left font-medium">指标</th>
              {years.map((y) => (
                <th key={y} className="px-3 py-2 text-right font-medium">
                  {y}
                </th>
              ))}
              <th className="px-3 py-2 text-left font-medium">走势</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={l.key} className={cn("border-t border-cardline/70", i % 2 ? "bg-canvas/60" : "bg-white")}>
                <td className={cn("sticky left-0 z-10 whitespace-nowrap px-3 py-1.5 text-zinc-700", i % 2 ? "bg-canvas" : "bg-white")}>
                  {l.label}
                  <span className="ml-1 text-[11px] text-zinc-400">{l.unit}</span>
                </td>
                {years.map((y) => {
                  const v = l.series[y];
                  const change = l.kind === "level" ? yoyOf(l.series, y) : null;
                  const landMark = l.formula && incomplete.has(y);
                  return (
                    <td key={y} className="px-2 py-1">
                      <ValueButton
                        value={
                          v == null ? (
                            <span className="text-zinc-300">—</span>
                          ) : (
                            <span className={cn(l.kind === "rate" && rateClass(v))}>
                              {l.kind === "rate" ? `${v.toFixed(1)}%` : fmtNum(v, l.digits)}
                              {landMark ? <span title="这一年的明细没取全，偏低">*</span> : null}
                            </span>
                          )
                        }
                        sub={change != null ? signedPct(change) : undefined}
                        onOpen={
                          v == null
                            ? undefined
                            : () =>
                                evidence.open(
                                  srcEvidence({
                                    title: `${city} · ${y}年 ${l.label}`,
                                    value: l.kind === "rate" ? `${v.toFixed(1)}%` : fmtNum(v, 2),
                                    unit: l.kind === "rate" ? undefined : l.unit,
                                    rows: (l.rows ?? []).filter((r) => String(r.year) === y),
                                    formula: l.formula,
                                  }),
                                )
                        }
                      />
                    </td>
                  );
                })}
                <td className="px-3 py-1">
                  <Sparkline values={years.map((y) => l.series[y])} zero={l.kind === "rate"} highlight />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-1.5 text-[12px] leading-5 text-zinc-500">
        {trend ? `GDP 实际增速${trendSentence(trend)}。` : ""}
        数值下方小字为比上年变化（名义）；实际增速按可比价计算，来自统计年鉴「上年=100」指数。
        {incomplete.size ? " 带 * 的年份土地明细没取全，宗数和面积偏低。" : ""}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 区县逐年趋势                                                                 */
/* -------------------------------------------------------------------------- */

export interface TrendRow {
  name: string;
  isTarget: boolean;
  d: LandRegionDataDistrict;
}

type TrendKey =
  | "gdp_real"
  | "gdp"
  | "secondary"
  | "output"
  | "firms"
  | "sme"
  | "population"
  | "new_reg"
  | "electricity"
  | "land_parcels"
  | "land_area"
  | "land_price"
  | "land_small";

interface TrendMetric {
  key: TrendKey;
  label: string;
  unit: (d: LandRegionDataDistrict) => string;
  digits: number;
  /** rate: 本身是增速; level: 体量(可切同比); count: 逐年量(汇总为合计); share: 占比 */
  kind: "rate" | "level" | "count" | "share";
  series: (d: LandRegionDataDistrict) => Series;
  /** 后端给的同比(只在同一来源内算); 不给则按序列自己算 */
  yoy?: (d: LandRegionDataDistrict) => Series;
  /** 某年数值来源与其他年份不同时的小标记 */
  mark?: (d: LandRegionDataDistrict, year: string) => string;
  sources?: (d: LandRegionDataDistrict, year: string) => LandRegionSrc[];
  formula?: string;
  note: string;
}

const industryYear = (d: LandRegionDataDistrict, year: string) => (d.industry.industry_years ?? []).find((x) => String(x.year) === year);

const industrySources = (unit: string) => (d: LandRegionDataDistrict, year: string): LandRegionSrc[] => {
  const fromYb = industryYear(d, year)?.source === "yearbook";
  return (d.sources.industry ?? []).filter(
    (r) => String(r.year) === year && r.unit === unit && (fromYb ? r.sub === "省年鉴" : r.sub !== "省年鉴"),
  );
};

const byYear = (key: keyof LandRegionDataDistrict["sources"]) => (d: LandRegionDataDistrict, year: string): LandRegionSrc[] =>
  (d.sources[key] ?? []).filter((r) => String(r.year) === year);

const TREND_METRICS: TrendMetric[] = [
  {
    key: "gdp_real",
    label: "GDP 实际增速",
    unit: () => "%",
    digits: 1,
    kind: "rate",
    series: (d) => d.economy.gdp_real_growth ?? {},
    sources: byYear("gdp_growth"),
    note: "按可比价计算的官方增速（统计年鉴「上年=100」指数减 100），年鉴还没出的最新一年取各区统计公报。近三年均值比此前三年变动 1.5 个百分点以上记为加快或放缓，研判按这个判断走势。",
  },
  {
    key: "gdp",
    label: "GDP",
    unit: () => "亿元",
    digits: 0,
    kind: "level",
    series: (d) => d.economy.gdp_series ?? {},
    sources: byYear("gdp"),
    note: "当年价。2024 年起为第五次全国经济普查后的口径，和 2023 年及以前的数不能直接比，2023→2024 的升降不代表真实增减。名义同比含价格因素，看实际增长请用「GDP 实际增速」。",
  },
  {
    key: "secondary",
    label: "二产增速",
    unit: () => "%",
    digits: 1,
    kind: "rate",
    series: (d) => d.economy.secondary_growth ?? {},
    sources: byYear("secondary_growth"),
    note: "第二产业增加值实际增速（可比价），反映工业和建筑业的走势。",
  },
  {
    key: "output",
    label: "规上工业产值",
    unit: () => "亿元",
    digits: 0,
    kind: "level",
    series: (d) => Object.fromEntries((d.industry.industry_years ?? []).map((x) => [String(x.year), x.output])),
    yoy: (d) => Object.fromEntries((d.industry.industry_years ?? []).map((x) => [String(x.year), x.output_yoy_pct])),
    mark: (d, y) => (industryYear(d, y)?.source === "own" ? "公报" : ""),
    sources: industrySources("亿元"),
    note: "各区同一口径取广东统计年鉴县区表；年鉴还没出的最新年份接本区统计公报或采集库（标「公报」）。两套数常差几个百分点，同比只在同一来源内计算。",
  },
  {
    key: "firms",
    label: "规上企业数",
    unit: () => "家",
    digits: 0,
    kind: "level",
    series: (d) => Object.fromEntries((d.industry.industry_years ?? []).map((x) => [String(x.year), x.firms])),
    mark: (d, y) => (industryYear(d, y)?.source === "own" ? "公报" : ""),
    sources: industrySources("家"),
    note: "规模以上工业企业单位数，口径同规上工业产值。",
  },
  {
    key: "sme",
    label: "中小企业数",
    unit: () => "家",
    digits: 0,
    kind: "level",
    series: (d) => d.industry.sme?.total ?? {},
    sources: byYear("size"),
    note: "规模以上工业里的中型 + 小微型企业数（区统计年鉴按企业规模分的表），是产业园的客户基础；大型企业数见「工业」页签。",
  },
  {
    key: "population",
    label: "常住人口",
    unit: () => "万人",
    digits: 1,
    kind: "level",
    series: (d) => d.people.pop_series ?? {},
    sources: byYear("population"),
    note: "年末常住人口。2020 年人口普查前后常有口径修订，看走势以普查后年份为主。",
  },
  {
    key: "new_reg",
    label: "新登记",
    unit: () => "户",
    digits: 0,
    kind: "level",
    series: (d) => d.new_reg.series ?? {},
    sources: byYear("new_reg"),
    note: "各区公布口径不同（新登记企业 / 含个体户的市场主体 / 年末存量），只看各区自己的走势，不横向比数量。",
  },
  {
    key: "electricity",
    label: "用电量",
    unit: (d) => d.electricity.unit || "",
    digits: 2,
    kind: "level",
    series: (d) => d.electricity.series_all ?? d.electricity.series ?? {},
    sources: byYear("electricity"),
    note: "优先工业用电量；区里不公布工业用电的用全社会用电量代替（见「用电」页签的口径列）。",
  },
  {
    key: "land_parcels",
    label: "供地宗数",
    unit: () => "宗",
    digits: 0,
    kind: "count",
    series: (d) => landSeries(d.land?.by_year, (x) => x.parcels),
    formula: LAND_FORMULA,
    note: "工业用地招拍挂成交宗数（国信网逐宗明细）。",
  },
  {
    key: "land_area",
    label: "供地面积",
    unit: () => "亩",
    digits: 0,
    kind: "count",
    series: (d) => landSeries(d.land?.by_year, (x) => x.area_mu),
    formula: LAND_FORMULA,
    note: "工业用地招拍挂成交面积（国信网逐宗明细）。",
  },
  {
    key: "land_price",
    label: "工业地价",
    unit: () => "万元/亩",
    digits: 1,
    kind: "level",
    series: (d) => landSeries(d.land?.by_year, (x) => x.avg_price_wan_per_mu),
    formula: LAND_FORMULA,
    note: "当年成交总价 ÷ 成交面积；宗数少的年份受单宗影响大。",
  },
  {
    key: "land_small",
    label: "小地块占比",
    unit: () => "%",
    digits: 0,
    kind: "share",
    series: (d) => landSeries(d.land?.by_year, (x) => (x.small_share == null ? null : x.small_share * 100)),
    formula: LAND_FORMULA,
    note: "30 亩以下宗数占当年成交宗数的比例；过半说明在切小块面向中小企业供地。",
  },
];

export function DistrictTrendTable({
  rows,
  city,
  incompleteYears,
}: {
  rows: TrendRow[];
  city: string;
  incompleteYears?: string[];
}): JSX.Element {
  const evidence = useEvidence();
  const metrics = useMemo(
    () => TREND_METRICS.filter((m) => rows.some((r) => Object.values(m.series(r.d)).some((v) => v != null))),
    [rows],
  );
  const [key, setKey] = useState<TrendKey>("gdp_real");
  const [mode, setMode] = useState<"value" | "yoy">("value");
  const [sortYear, setSortYear] = useState<string | null>(null);
  const m = metrics.find((x) => x.key === key) ?? metrics[0];
  if (!m) return <p className="text-[13px] text-zinc-400">没有逐年数据。</p>;

  const incomplete = new Set(m.formula ? (incompleteYears ?? []) : []);
  const years = windowYears(rows.map((r) => m.series(r.d)));
  const showYoy = mode === "yoy" && m.kind === "level";
  const cell = (r: TrendRow, y: string): number | null => {
    const s = m.series(r.d);
    if (!showYoy) return s[y] ?? null;
    return m.yoy ? (m.yoy(r.d)[y] ?? null) : yoyOf(s, y);
  };
  const summary = (r: TrendRow): { value: number | null; text: string; sub: string } => {
    const s = m.series(r.d);
    if (m.kind === "rate") {
      const t = rateTrend(s);
      if (!t) return { value: null, text: "—", sub: "" };
      return {
        value: t.recent,
        text: `${t.recent.toFixed(1)}%`,
        sub: t.change != null ? `${trendWord(t.change)} · ${pp(t.change)}` : t.recentSpan,
      };
    }
    if (m.kind === "count") {
      const vals = years.map((y) => s[y]).filter((v): v is number => v != null);
      const total = vals.reduce((a, b) => a + b, 0);
      return vals.length ? { value: total, text: fmtNum(total, m.digits), sub: `${years[0]}–${years[years.length - 1]}` } : { value: null, text: "—", sub: "" };
    }
    if (m.kind === "share") return { value: null, text: "", sub: "" };
    const c = cagrOf(s, years);
    return c ? { value: c.pct, text: signedPct(c.pct), sub: c.span } : { value: null, text: "—", sub: "" };
  };
  const summaryLabel = { rate: "近三年均值", level: "期间年均", count: "合计", share: "" }[m.kind];
  const latest = years[years.length - 1] ?? "";
  const sortKey = (r: TrendRow): number | null => (sortYear === "summary" ? summary(r).value : cell(r, sortYear ?? latest));
  const sorted = [...rows].sort((a, b) => {
    if (a.isTarget !== b.isTarget) return a.isTarget ? -1 : 1;
    const va = sortKey(a);
    const vb = sortKey(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    return vb - va;
  });
  const fmtCell = (v: number): string => (showYoy ? signedPct(v) : m.kind === "rate" ? `${v.toFixed(1)}%` : m.kind === "share" ? `${v.toFixed(0)}%` : fmtNum(v, m.digits));

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex flex-wrap rounded-lg border border-cardline bg-canvas p-0.5">
          {metrics.map((x) => (
            <button
              key={x.key}
              type="button"
              onClick={() => {
                setKey(x.key);
                setSortYear(null);
              }}
              className={cn(
                "rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors",
                m.key === x.key ? "bg-white text-brand-900 shadow-sm" : "text-zinc-500 hover:text-brand-800",
              )}
            >
              {x.label}
            </button>
          ))}
        </div>
        {m.kind === "level" ? (
          <div className="inline-flex rounded-lg border border-cardline bg-white p-0.5">
            {(
              [
                ["value", "数值"],
                ["yoy", "同比"],
              ] as const
            ).map(([k, label]) => (
              <button
                key={k}
                type="button"
                onClick={() => setMode(k)}
                className={cn(
                  "rounded-md px-2.5 py-1 text-[12px]",
                  mode === k ? "bg-brand-50 font-medium text-brand-900" : "text-zinc-500 hover:text-brand-800",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        ) : null}
      </div>
      <div className="overflow-x-auto rounded-lg border border-cardline">
        <table className="w-full min-w-[640px] text-[13px]">
          <thead>
            <tr className="bg-cardhead text-[12px] text-zinc-500">
              <th className="sticky left-0 z-10 bg-cardhead px-3 py-2 text-left font-medium">区县</th>
              {years.map((y) => (
                <th
                  key={y}
                  onClick={() => setSortYear(y)}
                  className="cursor-pointer select-none whitespace-nowrap px-3 py-2 text-right font-medium hover:text-brand-800"
                >
                  {y}
                  {incomplete.has(y) ? "*" : ""}
                  {(sortYear ?? latest) === y ? " ↓" : ""}
                </th>
              ))}
              {summaryLabel ? (
                <th
                  onClick={() => setSortYear("summary")}
                  className="cursor-pointer select-none whitespace-nowrap px-3 py-2 text-right font-medium hover:text-brand-800"
                >
                  {summaryLabel}
                  {sortYear === "summary" ? " ↓" : ""}
                </th>
              ) : null}
              <th className="px-3 py-2 text-left font-medium">走势</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r, i) => {
              const sm = summary(r);
              const s = m.series(r.d);
              return (
                <tr
                  key={r.name}
                  className={cn("border-t border-cardline/70", r.isTarget ? "bg-brand-50" : i % 2 ? "bg-canvas/60" : "bg-white")}
                >
                  <td
                    className={cn(
                      "sticky left-0 z-10 whitespace-nowrap px-3 py-1.5",
                      r.isTarget ? "bg-brand-50 font-semibold text-brand-900" : i % 2 ? "bg-canvas text-zinc-700" : "bg-white text-zinc-700",
                    )}
                  >
                    {r.isTarget ? <span className="mr-1 text-brand-600">★</span> : null}
                    {r.name}
                  </td>
                  {years.map((y) => {
                    const v = cell(r, y);
                    const mark = m.mark?.(r.d, y) ?? "";
                    const unit = m.unit(r.d);
                    return (
                      <td key={y} className="px-2 py-1">
                        <ValueButton
                          value={
                            v == null ? (
                              <span className="text-zinc-300">—</span>
                            ) : (
                              <span className={cn((m.kind === "rate" || showYoy) && rateClass(v))}>{fmtCell(v)}</span>
                            )
                          }
                          sub={mark || undefined}
                          onOpen={
                            s[y] == null
                              ? undefined
                              : () =>
                                  evidence.open(
                                    srcEvidence({
                                      title: `${city} · ${r.name} · ${y}年 ${m.label}`,
                                      value: m.kind === "rate" || m.kind === "share" ? `${(s[y] as number).toFixed(1)}%` : fmtNum(s[y], 2),
                                      unit: m.kind === "rate" || m.kind === "share" ? undefined : unit,
                                      rows: m.sources?.(r.d, y) ?? [],
                                      formula: m.formula,
                                      basis: [m.note],
                                    }),
                                  )
                          }
                        />
                      </td>
                    );
                  })}
                  {summaryLabel ? (
                    <td className="px-2 py-1">
                      <ValueButton
                        value={<span className={cn(m.kind !== "count" && rateClass(sm.value))}>{sm.text}</span>}
                        sub={sm.sub || undefined}
                      />
                    </td>
                  ) : null}
                  <td className="px-3 py-1">
                    <Sparkline values={years.map((y) => s[y])} zero={m.kind === "rate"} highlight={r.isTarget} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2.5 text-[12px] leading-5 text-zinc-400">
        点开数值可看来源和原文；点年份表头可按该年排序。{m.note}
        {incomplete.size ? " 带 * 的年份土地明细没取全，宗数和面积偏低。" : ""}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* 分行业逐年                                                                   */
/* -------------------------------------------------------------------------- */

const SECTOR_PREVIEW = 12;

export function SectorYearTable({
  items,
  growthOnly,
  title,
  sources,
  showRank = false,
  maxYears = 5,
  firms = false,
}: {
  items: LandRegionSectorFull[];
  growthOnly: boolean;
  /** 分行业企业数(家)而不是产值 */
  firms?: boolean;
  /** 证据面板标题前缀, 如「广州市」「广州市 · 黄埔区」 */
  title: string;
  sources?: LandRegionSrc[];
  showRank?: boolean;
  maxYears?: number;
}): JSX.Element | null {
  const evidence = useEvidence();
  const [all, setAll] = useState(false);
  if (!items.length) return null;
  const years = windowYears(items.map((s) => s.series ?? {}), maxYears);
  const shown = all ? items : items.slice(0, SECTOR_PREVIEW);
  const fmtV = (v: number): string => (growthOnly ? signedPct(v) : fmtNum(v, firms ? 0 : 1));
  const valueLabel = growthOnly ? "增速" : firms ? "企业数（家）" : "产值（亿元）";
  const open = (s: LandRegionSectorFull, year: string | null, v: number): void =>
    evidence.open(
      srcEvidence({
        title: `${title} · ${year ? `${year}年 ` : ""}${s.sector}${firms ? " 企业数" : ""}`,
        value: growthOnly ? signedPct(v) : fmtNum(v, firms ? 0 : 2),
        unit: growthOnly ? undefined : firms ? "家" : "亿元",
        rows: (sources ?? []).filter((r) => r.sub === s.sector && (!year || String(r.year) === year)),
        basis: [growthOnly ? "官方只公布行业增速，没有产值" : firms ? "规上工业分行业企业单位数" : "规上工业分行业总产值"],
      }),
    );

  return (
    <div>
      <div className="overflow-x-auto rounded-lg border border-cardline">
        <table className="w-full min-w-[620px] text-[13px]">
          <thead>
            <tr className="bg-cardhead text-[12px] text-zinc-500">
              <th className="sticky left-0 z-10 bg-cardhead px-3 py-2 text-left font-medium">行业</th>
              {years.length ? (
                years.map((y) => (
                  <th key={y} className="px-3 py-2 text-right font-medium">
                    {y}
                  </th>
                ))
              ) : (
                <th className="px-3 py-2 text-right font-medium">{valueLabel}</th>
              )}
              {growthOnly ? null : <th className="px-3 py-2 text-right font-medium">期间变化</th>}
              {showRank ? <th className="px-3 py-2 text-right font-medium">排序</th> : null}
              {showRank ? <th className="px-3 py-2 text-right font-medium">剥离重化工后</th> : null}
              <th className="px-3 py-2 text-left font-medium">走势</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((s, i) => (
              <tr key={s.sector} className={cn("border-t border-cardline/70", i % 2 ? "bg-canvas/60" : "bg-white")}>
                <td className={cn("sticky left-0 z-10 max-w-[16rem] px-3 py-1.5 text-zinc-800", i % 2 ? "bg-canvas" : "bg-white")}>
                  {s.sector}
                  {s.heavy ? <span className="ml-1.5 rounded bg-zinc-100 px-1 text-[11px] text-zinc-500">重化工</span> : null}
                </td>
                {years.length ? (
                  years.map((y) => {
                    const v = s.series?.[y];
                    return (
                      <td key={y} className="px-2 py-1">
                        <ValueButton
                          value={v == null ? <span className="text-zinc-300">—</span> : <span className={cn(growthOnly && rateClass(v))}>{fmtV(v)}</span>}
                          onOpen={v == null ? undefined : () => open(s, y, v)}
                        />
                      </td>
                    );
                  })
                ) : (
                  <td className="px-2 py-1">
                    <ValueButton
                      value={s.latest == null ? "—" : fmtV(s.latest)}
                      sub={s.latest_year ? `${s.latest_year}年` : undefined}
                      onOpen={s.latest == null ? undefined : () => open(s, null, s.latest as number)}
                    />
                  </td>
                )}
                {growthOnly ? null : (
                  <td className="px-3 py-1.5 text-right tabular-nums text-zinc-700">
                    <span className={rateClass(s.growth_pct)}>{s.growth_pct != null ? signedPct(s.growth_pct) : "—"}</span>
                    {s.span && s.growth_pct != null ? <span className="block text-[11px] text-zinc-400">{s.span}</span> : null}
                  </td>
                )}
                {showRank ? <td className="px-3 py-1.5 text-right tabular-nums text-zinc-600">{s.rank ?? "—"}</td> : null}
                {showRank ? (
                  <td className="px-3 py-1.5 text-right tabular-nums text-zinc-600">{s.heavy ? "剥离" : (s.rank_ex_heavy ?? "—")}</td>
                ) : null}
                <td className="px-3 py-1">
                  <Sparkline values={years.map((y) => s.series?.[y])} zero={growthOnly} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {items.length > SECTOR_PREVIEW ? (
        <button type="button" onClick={() => setAll((v) => !v)} className="mt-1.5 text-[12px] text-brand-700 hover:underline">
          {all ? "收起" : `展开全部 ${items.length} 个行业`}
        </button>
      ) : null}
    </div>
  );
}

/** 分行业页签下方: 选一个区看全部行业逐年 */
export function DistrictSectorDetail({ rows, city }: { rows: TrendRow[]; city: string }): JSX.Element | null {
  const withSectors = rows.filter((r) => r.d.sectors.items.length || r.d.sectors.firms?.length);
  const initial = withSectors.find((r) => r.isTarget)?.name ?? withSectors[0]?.name ?? "";
  const [pick, setPick] = useState(initial);
  const [view, setView] = useState<"output" | "firms">("output");
  const cur = withSectors.find((r) => r.name === pick) ?? withSectors[0];
  if (!cur) return null;
  const firmItems = cur.d.sectors.firms ?? [];
  const showFirms = (view === "firms" && firmItems.length > 0) || !cur.d.sectors.items.length;
  const recent = cur.d.sectors.growth_recent ?? [];
  return (
    <div className="mt-5">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium text-zinc-700">各区全部行业逐年</span>
        <div className="flex flex-wrap gap-1">
          {withSectors.map((r) => (
            <button
              key={r.name}
              type="button"
              onClick={() => setPick(r.name)}
              className={cn(
                "rounded-full px-2.5 py-0.5 text-[12px]",
                cur.name === r.name ? "bg-brand-900 text-white" : "bg-canvas text-zinc-600 hover:bg-brand-50 hover:text-brand-800",
              )}
            >
              {r.isTarget ? "★ " : ""}
              {r.name}
              <span className="ml-1 opacity-60">{r.d.sectors.items.length}</span>
            </button>
          ))}
        </div>
      </div>
      {firmItems.length && cur.d.sectors.items.length ? (
        <div className="mb-2 inline-flex rounded-md border border-cardline p-0.5 text-[12px]">
          {(
            [
              ["output", cur.d.sectors.growth_only ? "增速" : "产值"],
              ["firms", "企业数"],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              onClick={() => setView(k)}
              className={cn("rounded px-2.5 py-0.5", (k === "firms") === showFirms ? "bg-brand-900 text-white" : "text-zinc-600 hover:bg-brand-50")}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      {showFirms ? (
        <SectorYearTable key={`${cur.name}-firms`} items={firmItems} growthOnly={false} firms title={`${city} · ${cur.name}`} sources={cur.d.sources.sector_firms} />
      ) : (
        <SectorYearTable
          key={cur.name}
          items={cur.d.sectors.items}
          growthOnly={cur.d.sectors.growth_only}
          title={`${city} · ${cur.name}`}
          sources={cur.d.sources.sectors}
        />
      )}
      {recent.length && !showFirms ? (
        <div className="mt-3">
          <div className="mb-1.5 text-[12px] text-zinc-500">近年分行业增速（区统计公报只给增速，年份比上表的产值新）</div>
          <SectorYearTable
            key={`${cur.name}-recent`}
            items={recent}
            growthOnly
            title={`${city} · ${cur.name}`}
            sources={cur.d.sources.sector_growth}
          />
        </div>
      ) : null}
    </div>
  );
}
